// Explicit state machine per docs/ui-ux-plan.md §8. Every UI surface renders
// from this store; no ad-hoc booleans. Esc pops one state per press and never
// reaches OFF — exit is a separate, explicit transition.
//
// Submit is deferred (5s undo window) and closes the dialog immediately, so there
// is no in-flight "submitting" phase: SEND_OK re-arms straight from SELECTED.

export type Phase = 'OFF' | 'IDLE' | 'ARMED' | 'TARGETING' | 'DRAWING' | 'SELECTED'
export type Tool = 'picker' | 'marker'

/** Marker-tool region selection. `ancestor` is the anchor element (deepest common). */
export interface RegionSelection {
  box: { x: number; y: number; w: number; h: number }
  strokes: Array<Array<{ x: number; y: number }>>
  elements: Element[]
  ancestor: Element
}

export interface State {
  phase: Phase
  tool: Tool
  /** Currently hovered element (TARGETING) or locked element (SELECTED, element kind). */
  element: Element | null
  /** Locked region (SELECTED, region kind). */
  region: RegionSelection | null
  elementLost: boolean
}

export type Action =
  | { type: 'ENTER' }
  | { type: 'EXIT' }
  | { type: 'ARM'; tool?: Tool }
  | { type: 'DISARM' }
  | { type: 'HOVER'; element: Element }
  | { type: 'SELECT'; element: Element }
  | { type: 'RETARGET'; element: Element }
  | { type: 'DRAW_START' }
  | { type: 'DRAW_CONFIRM'; region: RegionSelection }
  | { type: 'DRAW_CLEAR' }
  | { type: 'SEND_OK' }
  | { type: 'ELEMENT_LOST' }
  | { type: 'ESC' }

export const initialState: State = {
  phase: 'OFF',
  tool: 'picker',
  element: null,
  region: null,
  elementLost: false,
}

export function reduce(s: State, a: Action): State {
  switch (a.type) {
    case 'ENTER':
      return s.phase === 'OFF' ? { ...initialState, phase: 'ARMED' } : s
    case 'EXIT':
      return { ...initialState, phase: 'OFF' }
    case 'ARM':
      return s.phase === 'OFF'
        ? s
        : { ...s, phase: 'ARMED', tool: a.tool ?? s.tool, element: null, region: null, elementLost: false }
    case 'DISARM':
      return s.phase === 'OFF' ? s : { ...s, phase: 'IDLE', element: null, region: null }
    case 'HOVER':
      return (s.phase === 'ARMED' || s.phase === 'TARGETING') && s.tool === 'picker'
        ? { ...s, phase: 'TARGETING', element: a.element }
        : s
    case 'SELECT':
      return s.phase === 'ARMED' || s.phase === 'TARGETING'
        ? { ...s, phase: 'SELECTED', element: a.element, region: null, elementLost: false }
        : s
    case 'RETARGET':
      return s.phase === 'SELECTED' ? { ...s, element: a.element, elementLost: false } : s
    case 'DRAW_START':
      return (s.phase === 'ARMED' || s.phase === 'TARGETING') && s.tool === 'marker' ? { ...s, phase: 'DRAWING' } : s
    case 'DRAW_CONFIRM':
      return s.phase === 'DRAWING'
        ? { ...s, phase: 'SELECTED', element: a.region.ancestor, region: a.region, elementLost: false }
        : s
    case 'DRAW_CLEAR':
      return s.phase === 'DRAWING' ? { ...s, phase: 'ARMED' } : s
    case 'SEND_OK':
      // Submit re-arms straight from SELECTED (dialog already closed; send deferred).
      // Stay armed for the next report — batch reviewing is the normal case.
      return s.phase === 'SELECTED' ? { ...s, phase: 'ARMED', element: null, region: null, elementLost: false } : s
    case 'ELEMENT_LOST':
      return s.phase === 'SELECTED' ? { ...s, elementLost: true } : s
    case 'ESC':
      // Stack pop with IDLE floor — Esc never exits evaluation mode.
      switch (s.phase) {
        case 'SELECTED':
          return { ...s, phase: 'ARMED', element: null, region: null, elementLost: false }
        case 'DRAWING':
          // Handled by the marker (clears ink first); if no ink, drop to ARMED.
          return { ...s, phase: 'ARMED' }
        case 'TARGETING':
        case 'ARMED':
          return { ...s, phase: 'IDLE', element: null }
        default:
          return s
      }
  }
}

export type Listener = (s: State, prev: State) => void

export function createStore(onChange: Listener) {
  let state = initialState
  return {
    get: () => state,
    dispatch(a: Action) {
      const prev = state
      state = reduce(state, a)
      if (state !== prev) onChange(state, prev)
    },
  }
}
